import { atom, useRecoilValue, type AtomEffect } from 'recoil'
import { getCaseImageVariantAvailability } from '@/api/caseImageVariants'
import { logger, EventCategory } from '@/utils/logger'

/**
 * How long the read of the switch waits, after each failed read, before it
 * is tried again: one wait per retry, each longer than the one before. Once
 * they are spent no timer reads again, so a backend that stays down costs
 * the page a handful of requests.
 */
export const AI_IMAGE_EDITS_AVAILABILITY_RETRY_DELAYS_MS = [
  2_000, 4_000, 8_000, 16_000, 32_000,
] as const

/** Window events after which a read that has not answered is tried at once. */
const READ_AGAIN_EVENTS = ['focus', 'online'] as const

/**
 * Asks the backend, once per app load, whether AI image edits are switched
 * on, the first time anything reads the switch. A failed read is logged and
 * tried again after each delay of
 * `AI_IMAGE_EDITS_AVAILABILITY_RETRY_DELAYS_MS`, and at once when the window
 * regains focus or the browser comes back online, until a read answers.
 * While no read has answered the answer stays unknown, which reads as off.
 * The first answer is kept: nothing reads again after it.
 */
const loadAiImageEditsEnabled: AtomEffect<boolean | null> = ({ setSelf }) => {
  let active = true
  let answered = false
  let reading = false
  let failures = 0
  let retryTimer: ReturnType<typeof setTimeout> | undefined

  const stopListening = () => {
    for (const event of READ_AGAIN_EVENTS) {
      window.removeEventListener(event, readSwitch)
    }
  }

  function readSwitch() {
    if (!active || answered || reading) return
    clearTimeout(retryTimer)
    retryTimer = undefined
    reading = true
    getCaseImageVariantAvailability()
      .then((availability) => {
        reading = false
        if (!active) return
        answered = true
        stopListening()
        setSelf(availability?.enabled === true)
      })
      .catch((error: unknown) => {
        reading = false
        if (!active) return
        const retryInMs = AI_IMAGE_EDITS_AVAILABILITY_RETRY_DELAYS_MS[failures]
        failures += 1
        logger.info({
          message: 'ai_image_edits_availability_read_failed',
          category: EventCategory.API,
          data: {
            status:
              (error as { response?: { status?: number } })?.response?.status ??
              null,
            failures,
            retryInMs: retryInMs ?? null,
          },
        })
        if (retryInMs !== undefined) {
          retryTimer = setTimeout(readSwitch, retryInMs)
        }
      })
  }

  for (const event of READ_AGAIN_EVENTS) {
    window.addEventListener(event, readSwitch)
  }
  readSwitch()
  return () => {
    active = false
    clearTimeout(retryTimer)
    stopListening()
  }
}

/**
 * The backend's answer: true or false once it has read the switch, null
 * while it is unknown. Kept here, unexported, so the switch is read only
 * through `useAiImageEditsEnabled`.
 */
const aiImageEditsEnabledAtom = atom<boolean | null>({
  key: 'aiImageEditsEnabled',
  default: null,
  effects_UNSTABLE: [loadAiImageEditsEnabled],
})

/**
 * Whether the MDL app offers AI image edits: the Remove Background tab in
 * Adjust Image, the Case Images grid's Enhance, Compare, Revert and Show
 * enhancement, the grid's low-resolution badge, the canvas low-resolution
 * warning's Enhance link, and a canvas photo's Revert changes and Show
 * enhancement. `GET /v2/case-image-variants/availability` answers for the
 * session the app runs in: false on MDL (an account session, or an MDL
 * order's edit link), and on an M3 order's edit link the switch an M3 admin
 * sets on Site Settings > AI Image Edits.
 * False until the backend answers true: while the read is in flight, while
 * failed reads are being retried, after the retries are spent, and when the
 * switch is off. Every entry point reads the switch through this hook, never
 * the atom or the route.
 */
export const useAiImageEditsEnabled = (): boolean =>
  useRecoilValue(aiImageEditsEnabledAtom) === true

/**
 * True only once the backend has answered that AI image edits are off;
 * false while the read is in flight, after it fails, and when the switch is
 * on. For a request that does no harm while the answer is unknown but must
 * already have answered if the switch then reads on (the variant pricing):
 * only a known off skips it, so no load made before the answer arrives
 * leaves an Enhance surface without its price.
 */
export const useAiImageEditsKnownOff = (): boolean =>
  useRecoilValue(aiImageEditsEnabledAtom) === false
